import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreVertical, Eye, Edit, UserX, Users, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateIT } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  attivo: 'bg-green-100 text-green-800',
  bozza: 'bg-slate-100 text-slate-800',
  sospeso: 'bg-yellow-100 text-yellow-800',
  cessato: 'bg-red-100 text-red-800',
  da_completare: 'bg-orange-100 text-orange-800'
};

const statusLabels = {
  attivo: 'Attivo',
  bozza: 'Bozza',
  sospeso: 'Sospeso',
  cessato: 'Cessato',
  da_completare: 'Da Completare'
};

export default function Volontari() {
  const navigate = useNavigate();
  const [volontari, setVolontari] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statoFilter, setStatoFilter] = useState('all');

  useEffect(() => {
    fetchVolontari();
  }, [statoFilter]);

  const fetchVolontari = async () => {
    try {
      let url = `${API_URL}/api/volontari`;
      const params = new URLSearchParams();
      if (statoFilter && statoFilter !== 'all') {
        params.append('stato', statoFilter);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setVolontari(data || []);
      } else {
        setVolontari([]);
      }
    } catch (error) {
      console.error('Error fetching volontari:', error);
      setVolontari([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCessazione = async (volontario) => {
    if (!window.confirm(`Sei sicuro di voler cessare ${volontario.nome} ${volontario.cognome}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/volontari/${volontario.volontario_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Volontario cessato. ${data.timestamp}`);
        fetchVolontari();
      } else {
        toast.error('Errore durante la cessazione');
      }
    } catch (error) {
      toast.error('Errore durante la cessazione');
    }
  };

  const filteredVolontari = volontari.filter(v => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      v.nome?.toLowerCase().includes(searchLower) ||
      v.cognome?.toLowerCase().includes(searchLower) ||
      v.codice_fiscale?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6" data-testid="volontari-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Volontari</h1>
          <p className="page-subtitle">Gestisci l'anagrafica dei volontari dell'associazione</p>
        </div>
        <Button className="btn-primary" onClick={() => navigate('/volontari/nuovo')} data-testid="add-volontario-btn">
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Volontario
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome, cognome o codice fiscale..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={statoFilter} onValueChange={setStatoFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-stato">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtra per stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="attivo">Attivi</SelectItem>
                <SelectItem value="bozza">Bozza</SelectItem>
                <SelectItem value="sospeso">Sospesi</SelectItem>
                <SelectItem value="da_completare">Da Completare</SelectItem>
                <SelectItem value="cessato">Cessati</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner w-8 h-8" />
            </div>
          ) : filteredVolontari.length === 0 ? (
            <div className="empty-state py-16">
              <Users className="empty-state-icon" />
              <p className="empty-state-title">Nessun volontario trovato</p>
              <p className="empty-state-text">
                {search || statoFilter !== 'all' 
                  ? 'Prova a modificare i filtri di ricerca'
                  : 'Inizia aggiungendo il primo volontario'}
              </p>
              {!search && statoFilter === 'all' && (
                <Button className="btn-primary mt-4" onClick={() => navigate('/volontari/nuovo')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi Volontario
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">N.</TableHead>
                    <TableHead>Nome e Cognome</TableHead>
                    <TableHead className="hidden md:table-cell">Codice Fiscale</TableHead>
                    <TableHead className="hidden lg:table-cell">Data Inizio</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-16">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVolontari.map((volontario) => (
                    <TableRow key={volontario.volontario_id} data-testid={`volontario-row-${volontario.volontario_id}`}>
                      <TableCell className="font-medium text-slate-500">
                        {volontario.progressivo}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">
                            {volontario.cognome} {volontario.nome}
                          </p>
                          <p className="text-sm text-slate-500 md:hidden">
                            {volontario.codice_fiscale || 'CF non inserito'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell font-mono text-sm">
                        {volontario.codice_fiscale || '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {formatDateIT(volontario.data_inizio_attivita) || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[volontario.stato] || 'bg-slate-100 text-slate-800'}>
                          {statusLabels[volontario.stato] || volontario.stato}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`action-menu-${volontario.volontario_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/volontari/${volontario.volontario_id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/volontari/${volontario.volontario_id}/modifica`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            {volontario.stato !== 'cessato' && (
                              <DropdownMenuItem 
                                onClick={() => handleCessazione(volontario)}
                                className="text-red-600"
                              >
                                <UserX className="w-4 h-4 mr-2" />
                                Cessa Attività
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {!loading && filteredVolontari.length > 0 && (
        <div className="text-sm text-slate-500 text-center">
          Visualizzati {filteredVolontari.length} volontari
          {statoFilter !== 'all' && ` (filtro: ${statusLabels[statoFilter]})`}
        </div>
      )}
    </div>
  );
}
